import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, GraduationCap, Euro, MousePointerClick, FileText, Plus, Trash2, Pencil, LogOut,
  BarChart3, Mail, ShoppingCart, X, Loader2, Upload, Eye, Rocket, Ticket, Send, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import Markdown from "@/components/Markdown";
import WeeklyArticleAdmin from "@/pages/WeeklyArticleAdmin";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  slug: "", lang: "it", category: "Guide", title: "", excerpt: "", cover_image: "",
  content: "", seo_title: "", seo_description: "", author: "Redazione Guardia",
  read_minutes: 6, affiliate_disclaimer: false, published: true,
};

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (user === false) navigate("/login");
  }, [user, navigate]);

  if (!user) return <div className="min-h-screen grid place-items-center bg-[#0c0d10] text-zinc-500">Loading…</div>;

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "content", label: "Content Studio", icon: FileText },
    { id: "subscribers", label: "Subscribers", icon: Mail },
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "coupons", label: "Coupons", icon: Ticket },
    { id: "waitlist", label: "Waitlist", icon: Rocket },
    { id: "drip", label: "Welcome Drip", icon: Send },
    { id: "weekly", label: "Articolo Giovedì", icon: Sparkles },
  ];

  return (
    <div className="min-h-screen bg-[#0c0d10]">
      <header className="border-b border-[#272b35] bg-[#0c0d10]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <span className="font-heading text-2xl font-black uppercase text-white">
            Guardia<span className="text-[#E63946]">.</span> <span className="text-sm text-zinc-500 font-sans">Admin</span>
          </span>
          <div className="flex items-center gap-4">
            <span className="text-xs text-zinc-500 hidden sm:block">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-sm text-zinc-300 hover:text-white"
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap gap-2 mb-8">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              onClick={() => setTab(tb.id)}
              data-testid={`admin-tab-${tb.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-semibold transition-colors ${
                tab === tb.id ? "bg-[#E63946] border-[#E63946] text-white" : "border-[#272b35] text-zinc-400 hover:text-white"
              }`}
            >
              <tb.icon size={16} /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "content" && <ContentStudio />}
        {tab === "subscribers" && <Subscribers />}
        {tab === "orders" && <Orders />}
        {tab === "coupons" && <CouponsAdmin />}
        {tab === "waitlist" && <WaitlistAdmin />}
        {tab === "drip" && <DripPreview />}
        {tab === "weekly" && <WeeklyArticleAdmin />}
      </div>
    </div>
  );
}

function Overview() {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/admin/metrics").then((r) => setM(r.data)); }, []);
  if (!m) return <Loader2 className="animate-spin text-[#E63946]" />;
  const cards = [
    { label: "Subscribers", value: m.subscribers, icon: Users, color: "#2563EB" },
    { label: "Blueprint Students", value: m.students, icon: GraduationCap, color: "#E63946" },
    { label: "Sales", value: m.sales_count, icon: ShoppingCart, color: "#F59E0B" },
    { label: "Revenue", value: `€${m.revenue.toFixed(2)}`, icon: Euro, color: "#10B981" },
    { label: "Affiliate Clicks", value: m.affiliate_clicks, icon: MousePointerClick, color: "#8B5CF6" },
    { label: "Articles", value: m.articles, icon: FileText, color: "#06B6D4" },
    { label: "Waitlist", value: m.waitlist ?? 0, icon: Rocket, color: "#F59E0B" },
  ];
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.label} data-testid={`metric-${c.label}`} className="bg-[#14161b] border border-[#272b35] rounded-xl p-5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-widest text-zinc-500">{c.label}</span>
              <c.icon size={18} style={{ color: c.color }} />
            </div>
            <div className="font-mono-data text-3xl font-bold text-white mt-3">{c.value}</div>
          </div>
        ))}
      </div>
      {m.clicks_by_item?.length > 0 && (
        <div className="mt-6 bg-[#14161b] border border-[#272b35] rounded-xl p-5">
          <h3 className="font-heading text-xl font-bold uppercase text-white mb-4">Affiliate clicks by product</h3>
          <div className="space-y-2">
            {m.clicks_by_item.map((c) => (
              <div key={c.item} className="flex items-center justify-between text-sm border-b border-[#272b35] pb-2">
                <span className="text-zinc-300 font-mono">{c.item}</span>
                <span className="text-[#E63946] font-bold">{c.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ContentStudio() {
  const [articles, setArticles] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/articles").then((r) => setArticles(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/admin/articles/${form.id}`, form);
      else await api.post("/admin/articles", form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-extrabold uppercase text-white">Articles</h2>
        <button
          onClick={() => setEditing(EMPTY)}
          data-testid="new-article-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase text-sm rounded-md"
        >
          <Plus size={16} /> New
        </button>
      </div>

      <div className="space-y-2">
        {articles.map((a) => (
          <div key={a.id} data-testid={`admin-article-${a.slug}-${a.lang}`} className="flex items-center justify-between bg-[#14161b] border border-[#272b35] rounded-lg p-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-[#272b35] text-zinc-300 rounded">{a.lang}</span>
                <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-[#E63946]/20 text-[#E63946] rounded">{a.category}</span>
                {!a.published && <span className="text-[10px] text-zinc-500">draft</span>}
              </div>
              <div className="text-white font-semibold truncate mt-1">{a.title}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => setEditing(a)} data-testid={`edit-article-${a.id}`} className="p-2 text-zinc-400 hover:text-white"><Pencil size={16} /></button>
              <button onClick={() => del(a.id)} data-testid={`delete-article-${a.id}`} className="p-2 text-zinc-400 hover:text-[#EF4444]"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      {editing && <ArticleEditor initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}

function ArticleEditor({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const coverInput = React.useRef(null);
  const contentInput = React.useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cats = ["Guide", "Technique", "Review", "History", "Curiosity"];

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return `${BACKEND}${data.url}`;
  };

  const onCover = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      set("cover_image", await upload(file));
      toast.success("Cover uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const onContentImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await upload(file);
      set("content", `${form.content}\n\n![${file.name}](${url})\n`);
      toast.success("Image inserted");
    } catch (err) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 overflow-y-auto p-4">
      <div className="max-w-6xl mx-auto bg-[#14161b] border border-[#272b35] rounded-xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#272b35] sticky top-0 bg-[#14161b] z-10">
          <h3 className="font-heading text-2xl font-bold uppercase text-white">{form.id ? "Edit" : "New"} article</h3>
          <div className="flex items-center gap-3">
            {uploading && <Loader2 size={18} className="animate-spin text-[#E63946]" />}
            <button onClick={onClose} data-testid="editor-close-btn" className="text-zinc-400 hover:text-white"><X /></button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-0">
          {/* FORM */}
          <div className="p-6 space-y-4 border-r border-[#272b35]">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Slug" testid="editor-slug"><input value={form.slug} onChange={(e) => set("slug", e.target.value)} className={inputCls} /></Field>
              <Field label="Language">
                <select value={form.lang} onChange={(e) => set("lang", e.target.value)} data-testid="editor-lang" className={inputCls}>
                  <option value="it">it</option><option value="en">en</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Category">
                <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="editor-category" className={inputCls}>
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Read minutes"><input type="number" value={form.read_minutes} onChange={(e) => set("read_minutes", parseInt(e.target.value) || 1)} className={inputCls} /></Field>
            </div>
            <Field label="Title" testid="editor-title"><input value={form.title} onChange={(e) => set("title", e.target.value)} className={inputCls} /></Field>
            <Field label="Cover image">
              <div className="flex gap-2">
                <input value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="URL or upload" className={inputCls} />
                <input ref={coverInput} type="file" accept="image/*" hidden onChange={onCover} data-testid="editor-cover-upload" />
                <button onClick={() => coverInput.current.click()} className="shrink-0 px-3 py-2 border border-[#272b35] rounded-md text-zinc-300 hover:text-white"><Upload size={16} /></button>
              </div>
            </Field>
            <Field label="Excerpt"><textarea value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} rows={2} className={inputCls} /></Field>
            <Field label="Content (Markdown · {{affiliate:item|label}})" testid="editor-content">
              <div className="flex justify-end mb-1.5">
                <input ref={contentInput} type="file" accept="image/*" hidden onChange={onContentImage} data-testid="editor-content-upload" />
                <button onClick={() => contentInput.current.click()} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white">
                  <Upload size={14} /> Insert image
                </button>
              </div>
              <textarea value={form.content} onChange={(e) => set("content", e.target.value)} rows={14} className={`${inputCls} font-mono text-xs`} />
            </Field>
            <Field label="SEO title"><input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={inputCls} /></Field>
            <Field label="SEO description"><textarea value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} rows={2} className={inputCls} /></Field>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={form.affiliate_disclaimer} onChange={(e) => set("affiliate_disclaimer", e.target.checked)} /> Affiliate disclaimer
              </label>
              <label className="flex items-center gap-2 text-sm text-zinc-300">
                <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} /> Published
              </label>
            </div>
          </div>

          {/* LIVE PREVIEW */}
          <div className="p-6 bg-[#0c0d10]/40">
            <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">
              <Eye size={14} /> Live preview
            </div>
            <div data-testid="editor-preview" className="max-w-none">
              {form.cover_image && (
                <img src={form.cover_image} alt="" className="w-full aspect-[21/9] object-cover rounded-lg border border-[#272b35] mb-4" />
              )}
              <span className="inline-block px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-white rounded bg-[#E63946]">{form.category}</span>
              <h1 className="mt-3 font-heading text-3xl font-black uppercase text-white leading-tight">{form.title || "Titolo…"}</h1>
              <p className="mt-2 text-sm text-zinc-400">{form.excerpt}</p>
              <div className="mt-5">
                <Markdown content={form.content || "_Scrivi il contenuto per vedere l'anteprima…_"} />
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-[#272b35] flex gap-3 sticky bottom-0 bg-[#14161b]">
          <button onClick={() => onSave(form)} data-testid="editor-save-btn" className="flex-1 px-6 py-3 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase rounded-md">Save</button>
          <button onClick={onClose} className="px-6 py-3 border border-[#272b35] text-zinc-300 rounded-md">Cancel</button>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#0c0d10] border border-[#272b35] rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-[#E63946]";
const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</label>
    <div className="mt-1.5">{children}</div>
  </div>
);

function Subscribers() {
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/admin/subscribers").then((r) => setSubs(r.data)); }, []);
  const filtered = subs.filter((s) => s.email.includes(q.toLowerCase()));

  const exportCsv = () => {
    const rows = [["email", "lang", "status", "created_at"], ...subs.map((s) => [s.email, s.lang, s.status, s.created_at])];
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "subscribers.csv";
    a.click();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email…" data-testid="subscriber-search" className={`${inputCls} max-w-xs`} />
        <button onClick={exportCsv} data-testid="export-csv-btn" className="px-4 py-2 border border-[#272b35] text-zinc-300 hover:text-white text-sm font-semibold rounded-md whitespace-nowrap">Export CSV</button>
      </div>
      <div className="bg-[#14161b] border border-[#272b35] rounded-xl overflow-hidden">
        {filtered.length === 0 && <p className="p-6 text-zinc-600 text-sm">No subscribers yet.</p>}
        {filtered.map((s) => (
          <div key={s.id} className="flex items-center justify-between px-5 py-3 border-b border-[#272b35] last:border-0 text-sm">
            <span className="text-white">{s.email}</span>
            <div className="flex items-center gap-4 text-xs font-mono text-zinc-500">
              <span className="uppercase">{s.lang}</span>
              <span className="text-[#10B981]">{s.status}</span>
              <span>{new Date(s.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
  return (
    <div className="bg-[#14161b] border border-[#272b35] rounded-xl overflow-hidden">
      {orders.length === 0 && <p className="p-6 text-zinc-600 text-sm">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o.id} data-testid={`order-${o.session_id}`} className="flex items-center justify-between px-5 py-3 border-b border-[#272b35] last:border-0 text-sm">
          <div>
            <div className="text-white">{o.email}</div>
            <div className="text-xs font-mono text-zinc-600">{new Date(o.created_at).toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-4 text-xs font-mono">
            <span className="text-white">€{(o.amount / 100).toFixed(2)}</span>
            <span className={o.payment_status === "paid" ? "text-[#10B981]" : "text-[#F59E0B]"}>{o.payment_status}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function CouponsAdmin() {
  const [coupons, setCoupons] = useState([]);
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState(20);
  const [maxRed, setMaxRed] = useState("");
  const [expires, setExpires] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/coupons").then((r) => setCoupons(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!code.trim()) return toast.error("Code required");
    setSaving(true);
    try {
      const body = { code: code.trim(), max_redemptions: maxRed ? parseInt(maxRed) : null, expires_at: expires || null };
      if (type === "percent") body.percent_off = parseFloat(value);
      else body.amount_off = parseFloat(value);
      await api.post("/admin/coupons", body);
      toast.success("Coupon created");
      setCode(""); setValue(20); setMaxRed(""); setExpires("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setSaving(false);
  };

  const deactivate = async (c) => {
    await api.delete(`/admin/coupons/${c.code}`);
    toast.success("Deactivated");
    load();
  };

  return (
    <div>
      <div className="bg-[#14161b] border border-[#272b35] rounded-xl p-6 mb-6">
        <h2 className="font-heading text-xl font-bold uppercase text-white mb-4">Create coupon</h2>
        <div className="grid sm:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Code</label>
            <input value={code} data-testid="coupon-code-input" onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="RING20" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Type</label>
            <select value={type} data-testid="coupon-type-select" onChange={(e) => setType(e.target.value)} className={`${inputCls} mt-1.5`}>
              <option value="percent">% off</option>
              <option value="amount">€ off</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">{type === "percent" ? "Percent" : "Amount €"}</label>
            <input type="number" value={value} data-testid="coupon-value-input" onChange={(e) => setValue(e.target.value)} className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Max uses (opt.)</label>
            <input type="number" value={maxRed} data-testid="coupon-max-input" onChange={(e) => setMaxRed(e.target.value)} placeholder="∞" className={`${inputCls} mt-1.5`} />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-500">Expiry (opt.)</label>
            <input type="date" value={expires} data-testid="coupon-expiry-input" onChange={(e) => setExpires(e.target.value)} className={`${inputCls} mt-1.5`} />
          </div>
        </div>
        <button onClick={create} disabled={saving} data-testid="create-coupon-btn" className="mt-4 inline-flex items-center gap-2 px-6 py-2.5 bg-[#E63946] hover:bg-[#D90429] text-white font-bold uppercase text-sm rounded-md disabled:opacity-60">
          {saving && <Loader2 size={16} className="animate-spin" />}<Plus size={16} /> Create
        </button>
      </div>

      <div className="bg-[#14161b] border border-[#272b35] rounded-xl overflow-hidden">
        {coupons.length === 0 && <p className="p-6 text-zinc-600 text-sm">No coupons yet.</p>}
        {coupons.map((c) => (
          <div key={c.id} data-testid={`coupon-${c.code}`} className="flex items-center justify-between px-5 py-3 border-b border-[#272b35] last:border-0 text-sm">
            <div className="flex items-center gap-3">
              <span className="font-mono font-bold text-white">{c.code}</span>
              <span className="text-[#10B981] font-mono text-xs">{c.percent_off ? `${c.percent_off}%` : `€${c.amount_off}`} off</span>
              {!c.active && <span className="text-xs text-zinc-600">inactive</span>}
              {c.expired && <span className="text-xs text-[#EF4444]">expired</span>}
            </div>
            <div className="flex items-center gap-4 text-xs font-mono text-zinc-500">
              {c.expires_at && <span className="text-[#F59E0B]">exp {new Date(c.expires_at).toLocaleDateString()}</span>}
              <span>{c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ""} used</span>
              {c.active && <button onClick={() => deactivate(c)} data-testid={`deactivate-${c.code}`} className="text-[#EF4444] hover:underline">Deactivate</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DripPreview() {
  const [lang, setLang] = useState("it");
  const [days, setDays] = useState([]);
  const [active, setActive] = useState(1);

  useEffect(() => {
    api.get("/admin/drip/preview", { params: { lang } }).then((r) => setDays(r.data.days));
  }, [lang]);

  const current = days.find((d) => d.day === active);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-2xl font-extrabold uppercase text-white">Welcome Drip · 5 days</h2>
        <div className="flex items-center bg-[#14161b] border border-[#272b35] rounded-full p-0.5">
          {["it", "en"].map((lc) => (
            <button key={lc} data-testid={`drip-lang-${lc}`} onClick={() => setLang(lc)} className={`px-3 py-1 text-xs font-mono uppercase rounded-full ${lang === lc ? "bg-[#E63946] text-white" : "text-zinc-400"}`}>{lc}</button>
          ))}
        </div>
      </div>
      <p className="text-sm text-zinc-400 mb-5">Sequenza automatica inviata 1 email/giorno agli iscritti. Cron: ogni giorno alle 08:00.</p>
      <div className="grid lg:grid-cols-[240px_1fr] gap-6">
        <div className="space-y-2">
          {days.map((d) => (
            <button
              key={d.day}
              data-testid={`drip-day-${d.day}`}
              onClick={() => setActive(d.day)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${active === d.day ? "bg-[#E63946] border-[#E63946] text-white" : "border-[#272b35] text-zinc-300 hover:border-[#3a3f4b]"}`}
            >
              <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">Day {d.day}</div>
              <div className="text-sm font-semibold mt-1 line-clamp-2">{d.subject}</div>
            </button>
          ))}
        </div>
        <div className="bg-white rounded-lg overflow-hidden border border-[#272b35]">
          {current && (
            <>
              <div className="bg-[#f4f4f5] px-4 py-2 border-b text-xs text-zinc-600 font-mono">
                <strong>Subject:</strong> {current.subject}
              </div>
              <iframe title="drip-preview" data-testid="drip-email-preview" srcDoc={current.html} className="w-full h-[520px] bg-white" />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function WaitlistAdmin() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/waitlist").then((r) => setRows(r.data)); }, []);
  const byProduct = rows.reduce((acc, r) => {
    acc[r.product] = (acc[r.product] || 0) + 1;
    return acc;
  }, {});
  return (
    <div>
      {Object.keys(byProduct).length > 0 && (
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          {Object.entries(byProduct).map(([p, n]) => (
            <div key={p} className="bg-[#14161b] border border-[#272b35] rounded-xl p-4">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{p}</div>
              <div className="font-mono-data text-2xl font-bold text-[#F59E0B] mt-2">{n}</div>
            </div>
          ))}
        </div>
      )}
      <div className="bg-[#14161b] border border-[#272b35] rounded-xl overflow-hidden">
        {rows.length === 0 && <p className="p-6 text-zinc-600 text-sm">No waitlist entries yet.</p>}
        {rows.map((r) => (
          <div key={r.id} data-testid={`waitlist-row-${r.email}`} className="px-5 py-3 border-b border-[#272b35] last:border-0 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-white">{r.email}</span>
              <div className="flex items-center gap-4 text-xs font-mono text-zinc-500">
                <span className="text-[#F59E0B]">{r.product}</span>
                <span>{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
            </div>
            {r.note && <p className="mt-1 text-xs text-zinc-400 italic">"{r.note}"</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
